import React, { ComponentType, ReactNode, useMemo } from 'react';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';

import { useCurrentProject } from './ProjectEntitlementsContext';
import { useCurrentAccountIdStore } from './stores/CurrentAccountIdStore';

type CurrentAccountIdContextValue = {
  accountId?: string;
};

/**
 * Context for current account ID.
 */
const CurrentAccountIdContext =
  getOrCreateGlobalContext<CurrentAccountIdContextValue>(
    CONTEXT_NAMES.CurrentAccountIdContext,
  );

const useCurrentAccountIdStoreWithValidation = () =>
  useStoreWithValidation<CurrentAccountIdContextValue>(
    CONTEXT_NAMES.CurrentAccountIdContext,
    {
      hook: 'useCurrentAccountIdContext',
      provider: 'CurrentAccountIdProvider',
      context: CurrentAccountIdContext,
    },
  );

/**
 * Returns the current account ID. Resolves it from the `CurrentAccountIdProvider` prop first, falling back to the account ID derived from the current project entitlements.
 *
 * The provider already writes the resolved value (`accountIdProp || currentProject?.accountId`) to the store, so this hook simply returns the store value.
 */
export const useCurrentAccountId = () => {
  const { accountId } = useCurrentAccountIdStoreWithValidation();
  return accountId;
};

type CurrentAccountIdProviderProps = {
  accountId?: string;
  children: ReactNode;
};

export const CurrentAccountIdProvider: ComponentType<
  CurrentAccountIdProviderProps
> = ({ children, accountId: accountIdProp }) => {
  const currentProject = useCurrentProject();
  const resolvedAccountId = accountIdProp || currentProject?.accountId;

  useCurrentAccountIdStore(resolvedAccountId);

  const value = useMemo(
    () => ({ accountId: resolvedAccountId }),
    [resolvedAccountId],
  );

  return (
    <CurrentAccountIdContext.Provider value={value}>
      {children}
    </CurrentAccountIdContext.Provider>
  );
};

CurrentAccountIdProvider.displayName = 'CurrentAccountIdProvider';
